import type { TSession } from "@/types/TSession";
import { flexRender } from "@tanstack/react-table";
import { useSessionsTable } from "@/hooks/dashboard/useSessionsTable";
import { SessionCloseConfirmDialog } from "./SessionCloseConfirmDialog";

interface SessionsTableProps {
  data: TSession[];
  onRefresh?: () => void;
}

export function SessionsTable({ data, onRefresh }: SessionsTableProps) {
  const {
    table,
    sessionToClose,
    isConfirmDialogOpen,
    handleCloseSession,
    closeConfirmDialog
  } = useSessionsTable(data, onRefresh);

  return (
    <>
      <div className="border rounded-lg w-full">
        <div className="overflow-y-auto max-h-[500px]">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      scope="col"
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      style={{ width: header.getSize() !== 0 ? `${header.getSize()}px` : undefined }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-2 whitespace-nowrap text-xs text-gray-500"
                      style={{ width: cell.column.getSize() !== 0 ? `${cell.column.getSize()}px` : undefined }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <SessionCloseConfirmDialog
        session={sessionToClose}
        isOpen={isConfirmDialogOpen}
        onConfirm={handleCloseSession}
        onCancel={closeConfirmDialog}
      />
    </>
  );
}